import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';

/**
 * Subconjunto de Markdown (negrito, links, listas, títulos #) sem dependências npm —
 * evita falhas de build quando node_modules do contentor não inclui react-markdown.
 */
function parseBoldSegments(text: string, keyPrefix: string): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    const m = part.match(/^\*\*([^*]+)\*\*$/);
    if (m) {
      return (
        <strong key={`${keyPrefix}-b-${i}`}>{m[1]}</strong>
      );
    }
    return <Fragment key={`${keyPrefix}-t-${i}`}>{part}</Fragment>;
  });
}

function parseInline(line: string, keyPrefix: string): React.ReactNode {
  const out: React.ReactNode[] = [];
  const re = /\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let linkIndex = 0;
  const copy = line;
  while ((m = re.exec(copy))) {
    const [full, label, href] = m;
    const before = copy.slice(last, m.index);
    out.push(...parseBoldSegments(before, `${keyPrefix}-pre-${m.index}`));
    const k = `${keyPrefix}-link-${linkIndex++}`;
    if (href.startsWith('/') && !href.startsWith('//')) {
      out.push(
        <Link key={k} to={href} className="kb-md-link-inline">
          {parseBoldSegments(label, `${k}-lbl`)}
        </Link>
      );
    } else {
      out.push(
        <a key={k} href={href} target="_blank" rel="noopener noreferrer" className="kb-md-link-inline">
          {parseBoldSegments(label, `${k}-lbl`)}
        </a>
      );
    }
    last = m.index + full.length;
  }
  out.push(...parseBoldSegments(copy.slice(last), `${keyPrefix}-tail`));
  return <>{out}</>;
}

function renderParagraphLines(lines: readonly string[], keyBase: number): React.ReactNode {
  return lines.map((ln, j) => (
    <Fragment key={`${keyBase}-ln-${j}`}>
      {j > 0 && <br />}
      {parseInline(ln, `p-${keyBase}-${j}`)}
    </Fragment>
  ));
}

function renderDocumentLines(lines: readonly string[]): React.ReactNode[] {
  const elements: React.ReactNode[] = [];
  let i = 0;
  let blockKey = 0;

  while (i < lines.length) {
    const raw = lines[i];
    if (!raw.trim()) {
      i++;
      continue;
    }

    const line = raw;
    const trimmed = line.trim();

    const headingMatch = trimmed.match(/^(#{1,4})\s+(.+)$/);
    if (headingMatch) {
      const depth = Math.min(headingMatch[1].length, 4);
      const hx = trimmed.replace(/^#+\s+/, '');
      const tag = (`h${depth}` as 'h1' | 'h2' | 'h3' | 'h4');
      elements.push(
        React.createElement(tag, { key: blockKey }, parseInline(hx, `h-${blockKey}`))
      );
      blockKey += 1;
      i += 1;
      continue;
    }

    if (/^\s*\d+\.\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s*/, ''));
        i++;
      }
      elements.push(
        <ol key={blockKey} className="kb-md-list kb-md-list--numbered">
          {items.map((item, j) => (
            <li key={j}>{parseInline(item, `ol-${blockKey}-${j}`)}</li>
          ))}
        </ol>
      );
      blockKey += 1;
      continue;
    }

    if (/^\s*[-*]\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s*/, ''));
        i++;
      }
      elements.push(
        <ul key={blockKey} className="kb-md-list kb-md-list--bullet">
          {items.map((item, j) => (
            <li key={j}>{parseInline(item, `ul-${blockKey}-${j}`)}</li>
          ))}
        </ul>
      );
      blockKey += 1;
      continue;
    }

    const paraLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^\s*\d+\.\s/.test(lines[i]) &&
      !/^\s*[-*]\s/.test(lines[i]) &&
      !/^#{1,4}\s/.test(lines[i].trim())
    ) {
      paraLines.push(lines[i]);
      i++;
    }
    const para = paraLines.join('\n');
    elements.push(
      <p key={blockKey} className="kb-md-paragraph">
        {renderParagraphLines(para.split('\n'), blockKey)}
      </p>
    );
    blockKey += 1;
  }

  return elements;
}

type Props = {
  text: string;
};

const KbAssistantMarkdown: React.FC<Props> = ({ text }) => {
  if (!text.trim()) {
    return null;
  }
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  return <div className="kb-assist-markdown">{renderDocumentLines(lines)}</div>;
};

export default KbAssistantMarkdown;
